import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { useUser } from "@/web/auth/useUser";
import { Button } from "@/web/components/primitives/button";
import { toaster } from "@/web/components/primitives/toast";
import {
  canSubmitPledge,
  EMPTY_PLEDGE_FORM_STATE,
  PledgeFormBody,
  type PledgeFormState,
} from "@/web/components/styled/PledgeForm";
import {
  buildPledgeFormState,
  hadExistingPledge,
  pledgeFormHasChanges,
  pledgeFormSubmitFields,
  type PledgeFormSeed,
} from "@/web/components/styled/pledgeFormState";
import { ErrorHandler } from "@/web/lib/errors";
import { tableQueryOptions, useProcedureMutation, useReducerMutation } from "@/web/lib/tanstack";
import { parseCardId } from "@/web/routes/-shareableCardGate/shareableCardGate";
import { procedures, reducers, tables } from "@tycho/web-bindings";

function usePledgeFormState(
  seed: PledgeFormSeed,
  { exactStoredNames = false }: { exactStoredNames?: boolean } = {},
) {
  const user = useUser();
  const initialFormState = buildPledgeFormState(seed, user, { exactStoredNames });
  const initialKey = JSON.stringify(initialFormState);
  const [formState, setFormState] = useState<PledgeFormState>(EMPTY_PLEDGE_FORM_STATE);
  const [initializedKey, setInitializedKey] = useState<string | null>(null);
  const initialFormStateRef = useRef<PledgeFormState | null>(null);

  // Re-seed only when serialized seed values change — not on every render
  // (`initialFormState` is a new object reference each time).
  useEffect(() => {
    const initial = JSON.parse(initialKey) as PledgeFormState;
    setFormState(initial);
    initialFormStateRef.current = initial;
    setInitializedKey(initialKey);
  }, [initialKey]);

  const hasChanges =
    initializedKey === initialKey &&
    pledgeFormHasChanges(formState, initialFormStateRef.current, { trimNames: true });

  const updateField = useCallback(
    <K extends keyof PledgeFormState>(key: K, value: PledgeFormState[K]) => {
      setFormState((current) => ({ ...current, [key]: value }));
    },
    [],
  );

  return { formState, updateField, initialFormStateRef, hasChanges };
}

type PledgeStepShellProps = {
  businessName: string;
  isPending: boolean;
  isSignedIn: boolean;
  currentPledgeAmount: number | undefined;
  formState: PledgeFormState;
  onFieldChange: <K extends keyof PledgeFormState>(key: K, value: PledgeFormState[K]) => void;
  canSubmit: boolean;
  isUpdate: boolean;
  onBack: () => void;
  onSubmit: () => void;
  /** When set, renders a "Delete Pledge" button for users with an existing pledge. */
  onDelete?: () => void;
  isDeleting?: boolean;
};

/**
 * Shared chrome for an inline pledge step: the {@link PledgeFormBody} field
 * stack plus a Back / Confirm footer. Lives inside the parent `NominateDialog`
 * shell (no nested `<Dialog>`), so the dialog header / typography is owned once
 * by the parent.
 */
function PledgeStepShell({
  businessName,
  isPending,
  isSignedIn,
  currentPledgeAmount,
  formState,
  onFieldChange,
  canSubmit,
  isUpdate,
  onBack,
  onSubmit,
  onDelete,
  isDeleting = false,
}: PledgeStepShellProps) {
  return (
    <div className="flex flex-col gap-4">
      <PledgeFormBody
        formState={formState}
        onFieldChange={onFieldChange}
        isPending={isPending || isDeleting}
        isSignedIn={isSignedIn}
        businessName={businessName}
        currentPledgeAmount={currentPledgeAmount}
        idPrefix="pledge"
      />
      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        <Button
          type="button"
          color="darkBlue"
          variant="outline"
          onClick={onBack}
          disabled={isPending || isDeleting}
        >
          Back
        </Button>
        <div className="flex items-center gap-2">
          {onDelete && isUpdate ? (
            <Button
              type="button"
              color="darkBlue"
              variant="outline"
              disabled={isPending || isDeleting}
              isLoading={isDeleting}
              onClick={onDelete}
              className="border-red-500 text-red-600 hover:bg-red-50"
            >
              Delete Pledge
            </Button>
          ) : null}
          <Button
            type="button"
            color="darkBlue"
            onClick={onSubmit}
            disabled={!canSubmit || isDeleting}
            isLoading={isPending}
          >
            {isPending ? "Submitting..." : isUpdate ? "Update pledge" : "Confirm pledge"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function NominationPledgeStep({
  nominatedBusinessId,
  businessName,
  initialAmount,
  initialReason,
  initialFirstName,
  initialLastName,
  initialConsentToDisplay,
  onBack,
  onSubmitted,
  onAnonymousPledge,
}: {
  nominatedBusinessId: string;
  businessName: string;
  initialAmount: number | undefined;
  initialReason: string | undefined;
  initialFirstName: string | undefined;
  initialLastName: string | undefined;
  initialConsentToDisplay: boolean | undefined;
  onBack: () => void;
  onSubmitted: () => void;
  onAnonymousPledge: () => void;
}) {
  const { isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const { formState, updateField, initialFormStateRef, hasChanges } = usePledgeFormState({
    amount: initialAmount,
    firstName: initialFirstName,
    lastName: initialLastName,
    supportiveMessage: initialReason,
    consentToDisplay: initialConsentToDisplay,
  });
  const { mutateAsync: expressInterest, isPending } = useReducerMutation(
    reducers.expressInterestInNomination,
  );
  const canSubmit = !isPending && hasChanges && canSubmitPledge(formState, isSignedIn ?? false);

  const { mutateAsync: removeInterest, isPending: isDeleting } = useReducerMutation(
    reducers.removeInterestInNomination,
  );

  const handleSubmit = useCallback(async () => {
    if (formState.amount === undefined) return;
    if (isPending || !canSubmitPledge(formState, isSignedIn ?? false)) return;
    const fields = pledgeFormSubmitFields(formState, isSignedIn ?? false);
    try {
      await expressInterest({
        nominatedBusinessId,
        intendedInvestmentDollars: formState.amount,
        reason: fields.supportiveMessage,
        email: fields.email,
        firstName: fields.firstName,
        lastName: fields.lastName,
        consentToDisplay: fields.consentToDisplay,
      });
      toaster({
        message: hadExistingPledge(initialFormStateRef.current)
          ? "Your pledge has been updated."
          : "Thanks — we've recorded your pledge.",
        variant: "success",
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.campaignBrowseNominations).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myNominationInterests).queryKey,
        }),
      ]);
      onSubmitted();
      if (!isSignedIn) onAnonymousPledge();
    } catch (error) {
      ErrorHandler.default(error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expressInterest,
    formState,
    initialFormStateRef,
    isPending,
    isSignedIn,
    nominatedBusinessId,
    onAnonymousPledge,
    onSubmitted,
    queryClient,
  ]);

  const handleDelete = useCallback(async () => {
    if (isDeleting) return;
    try {
      await removeInterest({
        nominatedBusinessId,
      });
      toaster({ message: "Your pledge has been removed.", variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.campaignBrowseNominations).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myNominationInterests).queryKey,
        }),
      ]);
      // Reset the form and clear the seeded-initial state so a subsequent
      // re-pledge starts from a clean EMPTY_PLEDGE_FORM_STATE.
      updateField("amount", undefined);
      initialFormStateRef.current = null;
      onSubmitted();
    } catch (error) {
      ErrorHandler.default(error);
    }
  }, [
    removeInterest,
    isDeleting,
    nominatedBusinessId,
    queryClient,
    toaster,
    updateField,
    initialFormStateRef,
    onSubmitted,
  ]);

  return (
    <PledgeStepShell
      businessName={businessName}
      isPending={isPending}
      isSignedIn={isSignedIn ?? false}
      currentPledgeAmount={initialAmount}
      formState={formState}
      onFieldChange={updateField}
      canSubmit={canSubmit}
      isUpdate={initialAmount !== undefined}
      onBack={onBack}
      onSubmit={() => void handleSubmit()}
      onDelete={isSignedIn ? () => void handleDelete() : undefined}
      isDeleting={isDeleting}
    />
  );
}

export function ShareableCardPledgeStep({
  cardId,
  businessName,
  initialInterest,
  onBack,
  onSubmitted,
  onAnonymousPledge,
}: {
  cardId: string;
  businessName: string;
  initialInterest:
    | {
        interestDollarAmount: number;
        firstName: string | null;
        lastName: string | null;
        supportiveMessage: string | null;
        consentToDisplay: boolean;
      }
    | undefined;
  onBack: () => void;
  onSubmitted: () => void;
  onAnonymousPledge: () => void;
}) {
  const { isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const parsedCardId = parseCardId(cardId);
  const { formState, updateField, initialFormStateRef, hasChanges } = usePledgeFormState(
    {
      amount: initialInterest?.interestDollarAmount,
      firstName: initialInterest?.firstName,
      lastName: initialInterest?.lastName,
      supportiveMessage: initialInterest?.supportiveMessage,
      consentToDisplay: initialInterest?.consentToDisplay,
    },
    { exactStoredNames: initialInterest !== undefined },
  );
  const { mutateAsync: setInterest, isPending } = useProcedureMutation(
    procedures.setInterestInBusinessCard,
  );

  const { mutateAsync: removeInterest, isPending: isDeleting } = useProcedureMutation(
    procedures.removeInterestInBusinessCard,
  );

  const canSubmit =
    !isPending &&
    !isDeleting &&
    parsedCardId !== undefined &&
    hasChanges &&
    canSubmitPledge(formState, isSignedIn ?? false);

  const handleSubmit = useCallback(async () => {
    if (!parsedCardId || formState.amount === undefined) return;
    if (isPending || !canSubmitPledge(formState, isSignedIn ?? false)) return;
    const fields = pledgeFormSubmitFields(formState, isSignedIn ?? false);
    try {
      await setInterest({
        cardId: parsedCardId,
        accessCode: "",
        interestDollarAmount: BigInt(formState.amount),
        firstName: fields.firstName,
        lastName: fields.lastName,
        email: fields.email,
        supportiveMessage: fields.supportiveMessage,
        consentToDisplay: fields.consentToDisplay,
      });
      toaster({
        message: hadExistingPledge(initialFormStateRef.current)
          ? "Your pledge has been updated."
          : "Thanks — we've recorded your pledge.",
        variant: "success",
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.campaignBrowseShareableCards).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myBusinessCardInterests).queryKey,
        }),
      ]);
      onSubmitted();
      if (!isSignedIn) onAnonymousPledge();
    } catch (error) {
      ErrorHandler.default(error);
    }
  }, [
    formState,
    initialFormStateRef,
    isPending,
    isSignedIn,
    onAnonymousPledge,
    onSubmitted,
    parsedCardId,
    queryClient,
    setInterest,
  ]);

  const handleDelete = useCallback(async () => {
    if (isDeleting || parsedCardId === undefined) return;
    try {
      await removeInterest({
        cardId: parsedCardId,
      });
      toaster({ message: "Your pledge has been removed.", variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.campaignBrowseShareableCards).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myBusinessCardInterests).queryKey,
        }),
      ]);
      // Reset the form and clear the seeded-initial state so a subsequent
      // re-pledge starts from a clean EMPTY_PLEDGE_FORM_STATE.
      updateField("amount", undefined);
      initialFormStateRef.current = null;
      onSubmitted();
    } catch (error) {
      ErrorHandler.default(error);
    }
  }, [
    removeInterest,
    isDeleting,
    parsedCardId,
    queryClient,
    toaster,
    updateField,
    initialFormStateRef,
    onSubmitted,
  ]);

  return (
    <PledgeStepShell
      businessName={businessName}
      isPending={isPending}
      isSignedIn={isSignedIn ?? false}
      currentPledgeAmount={initialInterest?.interestDollarAmount}
      formState={formState}
      onFieldChange={updateField}
      canSubmit={canSubmit}
      isUpdate={initialInterest !== undefined}
      onBack={onBack}
      onSubmit={() => void handleSubmit()}
      onDelete={isSignedIn ? () => void handleDelete() : undefined}
      isDeleting={isDeleting}
    />
  );
}
